<template>
  <div class="card">
    <div class="card-header">
      <div class="flex items-center justify-between w-full">
        <div>
          <h2 class="card-title flex items-center gap-2">
            <svg class="w-5 h-5" style="color: var(--text-tertiary);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Footer
          </h2>
          <p class="card-description">
            Footer that appears at the bottom of every page
          </p>
        </div>
      </div>
    </div>

    <!-- Toast notifications -->
    <div v-if="toast.show" class="fixed top-4 right-4 z-50">
      <div :class="['toast', toast.type]">
        <svg v-if="toast.type === 'success'" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        {{ toast.message }}
      </div>
    </div>

    <div class="card-body">
      <!-- Tab Navigation -->
      <div class="tabs-pills mb-4">
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'content' }]"
          @click="activeTab = 'content'">
          Content
        </button>
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'styles' }]"
          @click="activeTab = 'styles'">
          Styles
        </button>
        <button
          type="button"
          :class="['tab-pill', { active: activeTab === 'mobile' }]"
          @click="activeTab = 'mobile'">
          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Mobile
        </button>
      </div>

      <!-- Content Tab -->
      <div v-show="activeTab === 'content'">
        <!-- Template Selection -->
        <div class="mb-6">
          <p class="text-overline mb-2" style="color: var(--text-tertiary);">
            Template
          </p>
          <label class="form-label mb-3">Template Style</label>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label
              v-for="tmpl in templates"
              :key="templateValue(tmpl)"
              :class="[
                'template-card cursor-pointer rounded-lg border-2 p-3 transition-all hover:border-indigo-300',
                localTemplate === templateValue(tmpl) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              ]">
              <input
                v-model="localTemplate"
                type="radio"
                name="footer_template"
                :value="templateValue(tmpl)"
                class="sr-only">
              <div class="text-center">
                <div class="text-sm font-medium" style="color: var(--text-primary);">
                  {{ templateLabel(tmpl) }}
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Footer Columns -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <label class="form-label mb-0">Footer Columns</label>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="addColumn">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Column
            </button>
          </div>

          <div
            ref="columnsContainer"
            class="space-y-4">
            <div
              v-for="(column, colIndex) in columns"
              :key="column.id || colIndex"
              class="column-card bg-gray-50 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <div class="drag-handle cursor-move text-gray-400 hover:text-gray-600">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                  </svg>
                </div>

                <input
                  v-model="column.title"
                  type="text"
                  class="form-input flex-1"
                  placeholder="Column Title"
                  @input="emitUpdate">

                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  title="Duplicate column"
                  @click="duplicateColumn(colIndex)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>

                <button
                  type="button"
                  class="text-red-500 hover:text-red-700"
                  title="Remove column"
                  @click="removeColumn(colIndex)">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <!-- Column Type Selector -->
              <div class="ml-7 mb-3">
                <label class="form-label text-xs mb-1">Column Type</label>
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="colType in columnTypes"
                    :key="colType.value"
                    type="button"
                    :class="[
                      'px-2 py-1 rounded text-xs transition-all',
                      (column.type || 'manual') === colType.value
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    ]"
                    @click="changeColumnType(colIndex, colType.value)">
                    {{ colType.label }}
                  </button>
                </div>
              </div>

              <!-- Auto-populate Settings (for non-manual columns) -->
              <div v-if="column.type && column.type !== 'manual'" class="ml-7 mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-medium text-blue-700">Auto-populate Settings</span>
                  <button
                    type="button"
                    class="btn btn-sm bg-blue-500 text-white hover:bg-blue-600"
                    :disabled="syncingColumn === column.id"
                    @click="syncColumn(colIndex)">
                    <svg v-if="syncingColumn === column.id" class="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <svg v-else class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync Now
                  </button>
                </div>

                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="form-label text-xs">Max Items</label>
                    <input
                      v-model.number="column.auto_settings.max_items"
                      type="number"
                      min="1"
                      max="20"
                      class="form-input form-input-sm"
                      @change="emitUpdate">
                  </div>
                  <div class="flex items-end">
                    <label class="flex items-center gap-1 text-xs">
                      <input
                        v-model="column.auto_settings.show_all_link"
                        type="checkbox"
                        class="form-checkbox"
                        @change="emitUpdate">
                      Show "View All" link
                    </label>
                  </div>
                </div>

                <!-- Preview of auto-populated items -->
                <div v-if="column.links && column.links.length > 0" class="mt-2 pt-2 border-t border-blue-200">
                  <p class="text-xs text-blue-600 mb-1">
                    Current items ({{ column.links.length }}):
                  </p>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="link in column.links.slice(0, 5)"
                      :key="link.id"
                      class="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      {{ linkLabel(link) }}
                    </span>
                    <span v-if="column.links.length > 5" class="text-xs text-blue-500">
                      +{{ column.links.length - 5 }} more
                    </span>
                  </div>
                </div>
              </div>

              <!-- Manual Column Links (only for manual type) -->
              <div v-else class="ml-7 space-y-2">
                <div
                  v-for="(link, linkIndex) in column.links || []"
                  :key="link.id || linkIndex"
                  class="flex items-center gap-2">
                  <input
                    :value="linkLabel(link)"
                    type="text"
                    class="form-input form-input-sm flex-1"
                    placeholder="Link text"
                    @input="updateLinkLabel(link, $event.target.value)">
                  <input
                    v-model="link.url"
                    type="text"
                    class="form-input form-input-sm flex-1"
                    placeholder="/url"
                    @input="emitUpdate">
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600"
                    @click="removeLink(colIndex, linkIndex)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <button
                  type="button"
                  class="text-sm text-indigo-600 hover:text-indigo-800"
                  @click="addLink(colIndex)">
                  + Add link
                </button>
              </div>
            </div>
          </div>

          <p
            v-if="columns.length === 0"
            class="text-sm text-center py-4"
            style="color: var(--text-secondary);">
            No footer columns. Click "Add Column" to create one.
          </p>
        </div>

        <!-- Contact Information -->
        <div class="mb-6">
          <label class="form-label">Contact Information</label>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label class="form-label text-sm">Phone</label>
              <input
                v-model="localContent.contact.phone"
                type="text"
                class="form-input"
                placeholder="(555) 123-4567"
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">Email</label>
              <input
                v-model="localContent.contact.email"
                type="email"
                class="form-input"
                placeholder="info@example.com"
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">Address</label>
              <input
                v-model="localContent.contact.address"
                type="text"
                class="form-input"
                placeholder="123 Main St"
                @input="emitUpdate">
            </div>
          </div>
        </div>

        <!-- Social Media Links -->
        <div class="mb-6">
          <label class="form-label">Social Media</label>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="form-label text-sm">Facebook URL</label>
              <input
                v-model="localContent.social.facebook"
                type="url"
                class="form-input"
                placeholder="https://facebook.com/..."
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">Instagram URL</label>
              <input
                v-model="localContent.social.instagram"
                type="url"
                class="form-input"
                placeholder="https://instagram.com/..."
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">LinkedIn URL</label>
              <input
                v-model="localContent.social.linkedin"
                type="url"
                class="form-input"
                placeholder="https://linkedin.com/..."
                @input="emitUpdate">
            </div>
            <div>
              <label class="form-label text-sm">YouTube URL</label>
              <input
                v-model="localContent.social.youtube"
                type="url"
                class="form-input"
                placeholder="https://youtube.com/..."
                @input="emitUpdate">
            </div>
          </div>
        </div>

        <!-- Copyright -->
        <div class="mb-6">
          <label class="form-label">Copyright Text</label>
          <div class="relative">
            <input
              v-model="localContent.copyright"
              type="text"
              class="form-input"
              placeholder="© {year} {business_name}. All rights reserved."
              maxlength="200"
              @input="emitUpdate">
            <span class="absolute right-2 top-2 text-xs" style="color: var(--text-tertiary);">
              {{ (localContent.copyright || '').length }}/200
            </span>
          </div>
          <p class="text-xs mt-1" style="color: var(--text-secondary);">
            Use {year} for current year and {business_name} for your business name
          </p>
        </div>

        <!-- Bottom Links -->
        <div class="mb-6">
          <label class="form-label">Bottom Links</label>
          <div class="space-y-2">
            <label class="flex items-center gap-2">
              <input
                v-model="localContent.show_privacy_policy"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Privacy Policy</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                v-model="localContent.show_terms"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Terms & Conditions</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                v-model="localContent.show_sitemap"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Sitemap</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Styles Tab -->
      <div v-show="activeTab === 'styles'">
        <!-- Background -->
        <div class="mb-6">
          <label class="form-label">Background</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="bg in backgroundOptions"
              :key="bg.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.background === bg.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('background', bg.value)">
              {{ bg.label }}
            </button>
          </div>
        </div>

        <!-- Top Border -->
        <div class="mb-6">
          <label class="form-label">Top Border</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="border in borderOptions"
              :key="border.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.top_border === border.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('top_border', border.value)">
              {{ border.label }}
            </button>
          </div>
        </div>

        <!-- Link Style -->
        <div class="mb-6">
          <label class="form-label">Link Color</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="link in linkColorOptions"
              :key="link.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.link_color === link.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('link_color', link.value)">
              {{ link.label }}
            </button>
          </div>
        </div>

        <!-- Padding -->
        <div class="mb-6">
          <label class="form-label">Padding</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="padding in paddingOptions"
              :key="padding.value"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg border text-sm transition-all',
                localSettings.padding === padding.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateSetting('padding', padding.value)">
              {{ padding.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Tab -->
      <div v-show="activeTab === 'mobile'">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div>
              <p class="text-sm font-medium text-blue-800">
                Mobile-Specific Settings
              </p>
              <p class="text-sm text-blue-600">
                These settings only affect how your footer appears on mobile devices.
              </p>
            </div>
          </div>
        </div>

        <!-- Columns Behavior -->
        <div class="mb-6">
          <label class="form-label">Columns Behavior</label>
          <p class="text-sm text-gray-500 mb-3">
            How footer columns display on mobile devices
          </p>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="behavior in mobileColumnBehaviors"
              :key="behavior.value"
              type="button"
              :class="[
                'flex flex-col items-center p-4 rounded-lg border-2 transition-all',
                mobileSettings.columns_behavior === behavior.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
              @click="updateMobileSetting('columns_behavior', behavior.value)">
              <span class="text-2xl mb-2">{{ behavior.icon }}</span>
              <span class="text-sm font-medium">{{ behavior.label }}</span>
              <span class="text-xs text-gray-500 text-center mt-1">{{ behavior.description }}</span>
            </button>
          </div>
        </div>

        <!-- Default Open Column (for accordion mode) -->
        <div v-if="mobileSettings.columns_behavior === 'accordion'" class="mb-6">
          <label class="form-label">Default Open Column</label>
          <select
            v-model="mobileSettings.default_open_column"
            class="form-select"
            @change="emitUpdate">
            <option :value="null">
              All collapsed by default
            </option>
            <option
              v-for="column in columns"
              :key="column.id"
              :value="column.id">
              {{ column.title || 'Untitled Column' }}
            </option>
          </select>
        </div>

        <!-- Hidden Columns on Mobile -->
        <div class="mb-6">
          <label class="form-label">Column Visibility</label>
          <p class="text-sm text-gray-500 mb-3">
            Uncheck columns to hide them on mobile while keeping them visible on desktop
          </p>
          <div class="space-y-2 p-4 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
            <label
              v-for="column in columns"
              :key="column.id"
              class="flex items-center gap-2">
              <input
                :checked="!mobileSettings.hidden_columns.includes(column.id)"
                type="checkbox"
                class="form-checkbox"
                @change="toggleMobileColumn(column.id)">
              <span class="text-sm">{{ column.title || 'Untitled Column' }}</span>
            </label>
            <p v-if="columns.length === 0" class="text-sm text-gray-400">
              No columns to configure
            </p>
          </div>
        </div>

        <!-- Show/Hide Sections -->
        <div class="mb-6">
          <label class="form-label">Mobile Sections</label>
          <div class="space-y-3 p-4 bg-gray-50 rounded-lg">
            <label class="flex items-center gap-2">
              <input
                v-model="mobileSettings.show_contact"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Show contact information</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                v-model="mobileSettings.show_social"
                type="checkbox"
                class="form-checkbox"
                @change="emitUpdate">
              <span class="text-sm">Show social media links</span>
            </label>
          </div>
        </div>

        <!-- Simplified Mode -->
        <div class="mb-6 p-4 border border-amber-200 bg-amber-50 rounded-lg">
          <label class="flex items-center gap-2 mb-2">
            <input
              v-model="mobileSettings.simplified_mode"
              type="checkbox"
              class="form-checkbox"
              @change="emitUpdate">
            <span class="text-sm font-medium text-amber-800">Simplified Mobile Footer</span>
          </label>
          <p class="text-xs text-amber-700 ml-6">
            When enabled, shows only contact info, social links, and copyright on mobile.
            All column content will be hidden to save space.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useDragDrop } from './composables/useDragDrop'

const props = defineProps({
  content: { type: Object, required: true },
  settings: { type: Object, required: true },
  template: { type: String, required: true },
  templates: { type: Array, default: () => []},
  services: { type: Array, default: () => []},
  serviceAreas: { type: Array, default: () => []},
  businessName: { type: String, default: '' },
  apiEndpoints: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:content', 'update:settings', 'update:template', 'update'])

function templateValue(tmpl) {
  return tmpl?.value || tmpl?.type || ''
}

function templateLabel(tmpl) {
  return tmpl?.label || tmpl?.name || ''
}

function linkLabel(link) {
  return link?.label || link?.text || ''
}

function updateLinkLabel(link, value) {
  if (!link) {
    return
  }
  link.label = value
  link.text = value
  emitUpdate()
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns)) {
    return []
  }

  return columns.map((column) => {
    const normalized = { ...column }
    if (Array.isArray(normalized.links)) {
      normalized.links = normalized.links.map((link) => {
        const normalizedLink = { ...link }
        if (!normalizedLink.label && normalizedLink.text) {
          normalizedLink.label = normalizedLink.text
        }
        if (!normalizedLink.text && normalizedLink.label) {
          normalizedLink.text = normalizedLink.label
        }
        return normalizedLink
      })
    }
    return normalized
  })
}

function normalizeFooterContent(content) {
  return {
    ...content,
    columns: normalizeColumns(content.columns || [])
  }
}

// Local state
const activeTab = ref('content')
const columnsContainer = ref(null)
const syncingColumn = ref(null)

// Toast state
const toast = ref({ show: false, type: 'success', message: '' })

// Column types for smart footer columns
const columnTypes = [
  { value: 'manual', label: 'Manual' },
  { value: 'services', label: 'Services' },
  { value: 'service_areas', label: 'Service Areas' },
  { value: 'locations', label: 'Locations' }
]

// Local copies for v-model
const localContent = ref(normalizeFooterContent({
  columns: [],
  contact: { phone: '', email: '', address: '' },
  social: { facebook: '', instagram: '', linkedin: '', youtube: '' },
  copyright: '',
  show_privacy_policy: true,
  show_terms: true,
  show_sitemap: false,
  ...props.content
}))

const localSettings = ref({
  background: 'dark',
  top_border: 'none',
  link_color: 'muted',
  padding: 'normal',
  ...props.settings
})

const localTemplate = ref(props.template)

// Mobile settings state
const mobileSettings = ref({
  columns_behavior: 'accordion',
  default_open_column: null,
  hidden_columns: [],
  show_contact: true,
  show_social: true,
  simplified_mode: false,
  ...props.content.mobile
})

// Mobile column behavior options
const mobileColumnBehaviors = [
  { value: 'accordion', label: 'Accordion', icon: '📑', description: 'Collapsible sections' },
  { value: 'stacked', label: 'Stacked', icon: '📚', description: 'All expanded' },
  { value: 'single', label: 'Single', icon: '📄', description: 'One at a time' }
]

// Columns computed
const columns = computed({
  get: () => localContent.value.columns || [],
  set: (val) => {
    localContent.value.columns = val
    emitUpdate()
  }
})

// Options
const backgroundOptions = [
  { value: 'dark', label: 'Dark' },
  { value: 'darker', label: 'Darker' },
  { value: 'surface', label: 'Light' },
  { value: 'surface-sunken', label: 'Gray' }
]

const borderOptions = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'bold', label: 'Bold' }
]

const linkColorOptions = [
  { value: 'muted', label: 'Muted' },
  { value: 'standard', label: 'Standard' },
  { value: 'inverted', label: 'Inverted' }
]

const paddingOptions = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'spacious', label: 'Spacious' }
]

// Drag and drop
const { initSortable, reorderArray, generateId } = useDragDrop()

// Add new column
function addColumn() {
  if (!localContent.value.columns) {
    localContent.value.columns = []
  }
  localContent.value.columns.push({
    id: generateId(),
    title: 'New Column',
    type: 'manual',
    links: []
  })
  emitUpdate()
}

// Remove column
function removeColumn(index) {
  localContent.value.columns.splice(index, 1)
  emitUpdate()
}

// Duplicate column
function duplicateColumn(index) {
  const original = localContent.value.columns[index]
  const duplicate = {
    ...JSON.parse(JSON.stringify(original)),
    id: generateId(),
    title: `${original.title} (Copy)`
  }
  localContent.value.columns.splice(index + 1, 0, duplicate)
  emitUpdate()
}

// Add link to column
function addLink(columnIndex) {
  if (!localContent.value.columns[columnIndex].links) {
    localContent.value.columns[columnIndex].links = []
  }
  localContent.value.columns[columnIndex].links.push({
    id: generateId(),
    label: '',
    text: '',
    url: '/'
  })
  emitUpdate()
}

// Remove link from column
function removeLink(columnIndex, linkIndex) {
  localContent.value.columns[columnIndex].links.splice(linkIndex, 1)
  emitUpdate()
}

// Update setting
function updateSetting(key, value) {
  localSettings.value[key] = value
  emitUpdate()
}

// Update mobile setting
function updateMobileSetting(key, value) {
  mobileSettings.value[key] = value
  emitUpdate()
}

// Toggle mobile column visibility
function toggleMobileColumn(columnId) {
  const hiddenColumns = mobileSettings.value.hidden_columns || []
  const index = hiddenColumns.indexOf(columnId)
  if (index === -1) {
    // Add to hidden
    mobileSettings.value.hidden_columns = [...hiddenColumns, columnId]
  } else {
    // Remove from hidden
    mobileSettings.value.hidden_columns = hiddenColumns.filter((id) => id !== columnId)
  }
  emitUpdate()
}

// Show toast notification
function showToast(message, type = 'success') {
  toast.value = { show: true, type, message }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

// Change column type (smart footer columns)
async function changeColumnType(columnIndex, newType) {
  const column = localContent.value.columns[columnIndex]
  if (!column || !column.id) return

  const oldType = column.type || 'manual'
  if (oldType === newType) return

  // Initialize auto_settings if switching to auto type
  if (newType !== 'manual' && !column.auto_settings) {
    column.auto_settings = {
      max_items: 8,
      show_all_link: true,
      hidden_ids: [],
      category_filter: null
    }
  }

  // Call API to change type
  const endpoint = props.apiEndpoints.change_footer_column_type
  if (!endpoint) {
    // Fallback: just update locally
    column.type = newType
    emitUpdate()
    return
  }

  try {
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        column_id: column.id,
        column_type: newType,
        settings: column.auto_settings || {}
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      showToast(errorData.error || `Server error: ${response.status}`, 'error')
      return
    }

    const data = await response.json()

    if (data.success) {
      // Update local column with response data
      localContent.value.columns[columnIndex] = {
        ...column,
        ...data.column
      }
      emitUpdate()
      showToast(`Column type changed to ${newType}`)
    } else {
      showToast(data.error || 'Failed to change column type', 'error')
    }
  } catch (error) {
    console.error('Error changing column type:', error)
    showToast('Failed to change column type', 'error')
  }
}

// Sync column links from source (smart footer columns)
async function syncColumn(columnIndex) {
  const column = localContent.value.columns[columnIndex]
  if (!column || !column.id || !column.type || column.type === 'manual') return

  const endpoint = props.apiEndpoints.sync_footer_column
  if (!endpoint) {
    showToast('Sync endpoint not available', 'error')
    return
  }

  syncingColumn.value = column.id

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        column_id: column.id,
        source: column.type,
        settings: column.auto_settings || {}
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      showToast(errorData.error || `Server error: ${response.status}`, 'error')
      return
    }

    const data = await response.json()

    if (data.success) {
      // Update column links
      column.links = data.links || []
      column.view_all_link = data.view_all_link
      emitUpdate()
      showToast(`Synced ${data.links?.length || 0} items`)
    } else {
      showToast(data.error || 'Failed to sync column', 'error')
    }
  } catch (error) {
    console.error('Error syncing column:', error)
    showToast('Failed to sync column', 'error')
  } finally {
    syncingColumn.value = null
  }
}

// Emit updates to parent
function emitUpdate() {
  emit('update:content', {
    ...localContent.value,
    mobile: { ...mobileSettings.value }
  })
  emit('update:settings', { ...localSettings.value })
  emit('update')
}

// Watch for template changes
watch(localTemplate, (newVal) => {
  emit('update:template', newVal)
  emit('update')
})

// Watch for prop changes
watch(() => props.content, (newVal) => {
  localContent.value = normalizeFooterContent({
    ...localContent.value,
    ...newVal,
    contact: { ...localContent.value.contact, ...(newVal.contact || {}) },
    social: { ...localContent.value.social, ...(newVal.social || {}) }
  })
}, { deep: true })

watch(() => props.settings, (newVal) => {
  localSettings.value = { ...localSettings.value, ...newVal }
}, { deep: true })

watch(() => props.template, (newVal) => {
  localTemplate.value = newVal
})

watch(() => props.content.mobile, (newVal) => {
  if (newVal) {
    mobileSettings.value = { ...mobileSettings.value, ...newVal }
  }
}, { deep: true })

// Initialize sortable after mount
onMounted(() => {
  nextTick(() => {
    if (columnsContainer.value) {
      initSortable(columnsContainer.value, {
        onReorder: ({ oldIndex, newIndex }) => {
          localContent.value.columns = reorderArray(
            localContent.value.columns,
            oldIndex,
            newIndex
          )
          emitUpdate()
        }
      })
    }
  })
})
</script>

<style scoped>
.sortable-ghost {
  opacity: 0.4;
  background: #c8ebfb;
}

.sortable-chosen {
  background: #e5e7eb;
}
</style>
